import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, createColumnHelper, EmptyState, Flex, Group, Input, NavLink, Skeleton, Stack, StatusToken, Table, Text, Tooltip, useTable } from "@components/mantine";
import { PrivilegeLevels } from "@core/user";
import { useMemo, useState } from "react";
import { Locales } from "../../strings/Locales.js";
import { AccessLevelPicker } from "./AccessLevelPicker.js";
import { CanCreateCheckbox } from "./CanCreateCheckbox.js";
import { GranularPrivilegesTable } from "./granular-privileges/GranularPrivilegesTable.js";
import classes from "./PrivilegesTable.module.css";
import { noAccess } from "./PrivilegesTableConstants.js";
import { PrivilegesTableUtils } from "./PrivilegesTableUtils.js";
import { PrivilegesTableContextProvider } from "./PrivilegeTableContext.js";
import { UndoButton } from "./UndoButton.js";
var columnsHelper = createColumnHelper();
var nameColumn = columnsHelper.accessor("name", {
    id: "privilege-name",
    header: Locales.format("PrivilegesTable.column.name.header"),
    enableSorting: false
});
var scopeColumn = columnsHelper.accessor("level", {
    header: Locales.format("PrivilegesTable.column.level.header"),
    enableSorting: false,
    cell: function(param) {
        var row = param.row;
        var _level;
        var level = (_level = row.original.level) !== null && _level !== void 0 ? _level : PrivilegeLevels.Normal;
        return /*#__PURE__*/ _jsx(Text, {
            inherit: true,
            className: classes.scopeCell,
            children: Locales.formatOrHumanize("PrivilegesTable.column.level.".concat(level), {
                defaultTranslation: level
            })
        });
    }
});
var canCreateColumn = columnsHelper.display({
    id: "can-create-checkbox",
    header: Locales.format("PrivilegesTable.column.create.header"),
    size: 84,
    cell: function(param) {
        var row = param.row;
        return /*#__PURE__*/ _jsx(CanCreateCheckbox, _object_spread({}, row.original));
    }
});
var accessLevelColumn = columnsHelper.display({
    id: "access-level-picker",
    header: Locales.format("PrivilegesTable.column.access.header"),
    cell: function(param) {
        var row = param.row;
        return /*#__PURE__*/ _jsx(AccessLevelPicker, _object_spread({}, row.original));
    }
});
var undoButtonColumn = columnsHelper.display({
    id: "undo-button",
    cell: function(param) {
        var row = param.row;
        return /*#__PURE__*/ _jsx(UndoButton, _object_spread({}, row.original));
    },
    size: 84
});
export var PrivilegesTable = function(param) {
    var systemPrivileges = param.systemPrivileges, initialPrivileges = param.initialPrivileges, _selectedPrivileges = param.selectedPrivileges, selectedPrivileges = _selectedPrivileges === void 0 ? noAccess : _selectedPrivileges, _availablePrivileges = param.availablePrivileges, availablePrivileges = _availablePrivileges === void 0 ? noAccess : _availablePrivileges, _loading = param.loading, loading = _loading === void 0 ? false : _loading, _readOnly = param.readOnly, readOnly = _readOnly === void 0 ? false : _readOnly, _exclusivePrivileges = param.exclusivePrivileges, exclusivePrivileges = _exclusivePrivileges === void 0 ? noAccess : _exclusivePrivileges, onChange = param.onChange, _count = param.count, count = _count === void 0 ? "modified" : _count, error = param.error, _withCanCreate = param.withCanCreate, withCanCreate = _withCanCreate === void 0 ? true : _withCanCreate, _withScope = param.withScope, withScope = _withScope === void 0 ? false : _withScope;
    var ref = _sliced_to_array(useState([]), 2), customPrivileges = ref[0], setCustomPrivileges = ref[1];
    var ref1 = _sliced_to_array(useMemo(function() {
        return [
            PrivilegesTableUtils.getRowsFromPrivileges({
                systemPrivileges: systemPrivileges,
                availablePrivileges: availablePrivileges,
                initialPrivileges: initialPrivileges,
                exclusivePrivileges: exclusivePrivileges
            }),
            PrivilegesTableUtils.getTabsFromPrivileges(systemPrivileges)
        ];
    }, [
        systemPrivileges,
        availablePrivileges,
        initialPrivileges,
        exclusivePrivileges
    ]), 2), rows = ref1[0], tabs = ref1[1];
    var ref2 = _sliced_to_array(useState(tabs.includes("Content") ? "Content" : tabs[0]), 2), activeTab = ref2[0], setActiveTab = ref2[1];
    var selectedPrivilegesByRowId = PrivilegesTableUtils.groupPrivilegesByRowId(selectedPrivileges);
    var data = useMemo(function() {
        return rows.filter(function(row) {
            return row.tab === activeTab;
        }).sort(function(a, b) {
            return a.name.localeCompare(b.name);
        });
    }, [
        rows,
        activeTab
    ]);
    var table = useTable({
        initialState: {
            totalEntries: data.length
        }
    });
    var resolvedColumns = useMemo(function() {
        var cols = [
            nameColumn
        ];
        if (withScope) {
            cols.push(scopeColumn);
        }
        cols.push(accessLevelColumn);
        if (withCanCreate) {
            cols.push(canCreateColumn);
        }
        cols.push(undoButtonColumn, Table.CollapsibleColumn);
        return cols;
    }, [
        withScope,
        withCanCreate
    ]);
    return /*#__PURE__*/ _jsx(PrivilegesTableContextProvider, {
        value: {
            selectedPrivilegesByRowId: selectedPrivilegesByRowId,
            onChange: onChange,
            customPrivileges: customPrivileges,
            setCustomPrivileges: setCustomPrivileges,
            readOnly: readOnly
        },
        children: /*#__PURE__*/ _jsxs(Stack, {
            gap: "xs",
            children: [
                error ? /*#__PURE__*/ _jsx(Input.Error, {
                    children: error
                }) : null,
                /*#__PURE__*/ _jsxs(Flex, {
                    children: [
                        !table.isVacant ? /*#__PURE__*/ _jsx(Box, {
                            w: 256,
                            className: classes.navigation,
                            "data-testid": "privileges-table-navigation",
                            "data-loading": loading,
                            children: tabs.map(function(tab) {
                                var ref = _sliced_to_array(rows.reduce(function(tabInfo, row) {
                                    if (row.tab === tab) {
                                        var _tabInfo = _sliced_to_array(tabInfo, 3), totalCount = _tabInfo[0], changedCount = _tabInfo[1], hasWarning = _tabInfo[2];
                                        var _id;
                                        var selectedPrivilegesForRow = (_id = selectedPrivilegesByRowId[row.id]) !== null && _id !== void 0 ? _id : noAccess;
                                        totalCount += 1;
                                        if (PrivilegesTableUtils.hasChanged(row.initialPrivileges, selectedPrivilegesForRow)) {
                                            changedCount += 1;
                                        }
                                        if (!hasWarning) {
                                            hasWarning = PrivilegesTableUtils.getWarnings({
                                                selectedPrivileges: selectedPrivilegesForRow,
                                                initialPrivileges: row.initialPrivileges,
                                                availablePrivileges: row.availablePrivileges,
                                                exclusivePrivileges: row.exclusivePrivileges
                                            }).length > 0;
                                        }
                                        return [
                                            totalCount,
                                            changedCount,
                                            hasWarning
                                        ];
                                    }
                                    return tabInfo;
                                }, [
                                    0,
                                    0,
                                    false
                                ]), 3), totalRowsCount = ref[0], changedRowsCount = ref[1], rowHasWarning = ref[2];
                                return /*#__PURE__*/ _jsx(Skeleton, {
                                    visible: loading,
                                    className: classes.skeleton,
                                    children: /*#__PURE__*/ _jsx(NavLink, {
                                        component: "button",
                                        label: tab,
                                        active: tab === activeTab,
                                        onClick: function() {
                                            return setActiveTab(tab);
                                        },
                                        rightSection: /*#__PURE__*/ _jsxs(Group, {
                                            gap: "xs",
                                            children: [
                                                !readOnly && rowHasWarning ? /*#__PURE__*/ _jsx(StatusToken, {
                                                    size: "sm",
                                                    variant: "caution",
                                                    "data-testid": "warning-indicator"
                                                }) : null,
                                                count === "modified" && changedRowsCount > 0 ? /*#__PURE__*/ _jsx(Tooltip, {
                                                    label: Locales.format("PrivilegesTable.changesCount.tooltip", {
                                                        smart_count: changedRowsCount
                                                    }),
                                                    children: /*#__PURE__*/ _jsx(Text, {
                                                        fz: "xs",
                                                        children: "(".concat(changedRowsCount, ")")
                                                    })
                                                }) : null,
                                                count === "total" && /*#__PURE__*/ _jsx(Text, {
                                                    fz: "xs",
                                                    children: "(".concat(totalRowsCount, ")")
                                                })
                                            ]
                                        })
                                    }, tab)
                                }, tab);
                            })
                        }) : null,
                        /*#__PURE__*/ _jsx(Table, {
                            flex: 1,
                            store: table,
                            data: data,
                            columns: resolvedColumns,
                            loading: loading,
                            getRowId: function(param) {
                                var id = param.id;
                                return id;
                            },
                            getRowExpandedContent: function(datum) {
                                return /*#__PURE__*/ _jsx(GranularPrivilegesTable, _object_spread({}, datum));
                            },
                            children: /*#__PURE__*/ _jsx(Table.NoData, {
                                children: /*#__PURE__*/ _jsxs(EmptyState, {
                                    variant: "feedback",
                                    children: [
                                        /*#__PURE__*/ _jsx(EmptyState.Title, {
                                            children: Locales.format("PrivilegesTable.emptyState.title")
                                        }),
                                        /*#__PURE__*/ _jsx(EmptyState.Image, {})
                                    ]
                                })
                            })
                        })
                    ]
                })
            ]
        })
    });
};

//# sourceMappingURL=PrivilegesTable.js.map